import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Card, Input, Modal, Select, StatusPill, EmptyState, useToast } from '../../../design-system';
import { AUDIT_TYPES } from '../AuditorsManager';
import '../../styles/Auditors.css';

const typeLabel = (t) => AUDIT_TYPES.find((x) => x.value === t)?.label || t;

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

// Appoint auditors to this client, one per capacity. The auditor's remaining
// headroom is shown right in the picker, and the backend refuses an appointment
// that would breach their ceiling — so it can't be talked past here either.
function ClientAuditorsManager({ clientId, appointments = [], onChange }) {
  const toast = useToast();
  const [auditors, setAuditors] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [type, setType] = useState('statutory');
  const [auditorId, setAuditorId] = useState('');
  const [fy, setFy] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient
      .get('/api/auditors', { params: { active: 'true' } })
      .then((res) => setAuditors(res.data))
      .catch(() => {});
  }, []);

  // Only auditors who hold the chosen capacity can take the appointment.
  const eligible = useMemo(
    () => auditors.filter((a) => (a.types || []).includes(type)),
    [auditors, type]
  );

  const capacityOf = (a) => (a.capacity || []).find((c) => c.type === type);

  // "5 left of 60" / "no cap" — and whether they're full.
  const describe = (a) => {
    const cap = capacityOf(a);
    if (!cap || cap.limit == null) return 'no cap';
    return cap.remaining <= 0 ? `FULL — ${cap.assigned}/${cap.limit}` : `${cap.remaining} left of ${cap.limit}`;
  };
  const isFull = (a) => {
    const cap = capacityOf(a);
    return cap && cap.limit != null && cap.remaining <= 0;
  };

  const openAdd = () => {
    setType('statutory');
    setAuditorId('');
    setFy('');
    setError('');
    setModalOpen(true);
  };

  const appoint = async (e) => {
    e.preventDefault();
    if (!auditorId) { setError('Pick an auditor'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await apiClient.post(`/api/clients/${clientId}/auditors`, { auditorId, type, fy });
      onChange(res.data);
      // The appointment just consumed a slot, so the headroom shown in the
      // picker is now stale — pull the fresh counts.
      const fresh = await apiClient.get('/api/auditors', { params: { active: 'true' } });
      setAuditors(fresh.data);
      setModalOpen(false);
      toast.success('Auditor appointed');
    } catch (err) {
      // 409 = at their ceiling, or already this client's auditor for that type.
      setError(err.response?.data?.error || 'Failed to appoint auditor');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    try {
      const res = await apiClient.delete(`/api/clients/${clientId}/auditors/${a._id}`);
      onChange(res.data);
      const fresh = await apiClient.get('/api/auditors', { params: { active: 'true' } });
      setAuditors(fresh.data);
      toast.success('Appointment removed');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to remove appointment');
    }
  };

  return (
    <Card>
      <Card.Header
        title="Auditors"
        subtitle="Who audits this client, and in what capacity"
        action={<Button type="button" variant="primary" size="sm" onClick={openAdd}>+ Appoint Auditor</Button>}
      />

      {appointments.length === 0 ? (
        <EmptyState
          icon="🧾"
          title="No auditors appointed"
          description="Appoint a statutory, internal, tax or other auditor for this client."
          action={<Button type="button" variant="primary" size="sm" onClick={openAdd}>+ Appoint Auditor</Button>}
        />
      ) : (
        <div className="appointment-list">
          {appointments.map((a) => {
            // Populated by the API; falls back to the raw id if it ever isn't.
            const auditor = a.auditorId || {};
            return (
              <div key={a._id} className="appointment-row">
                <div className="appointment-identity">
                  <span className="appointment-name">{auditor.name || 'Unknown auditor'}</span>
                  <div className="appointment-meta">
                    {auditor.firmName && <span>{auditor.firmName}</span>}
                    {auditor.membershipNumber && <span>M. No. {auditor.membershipNumber}</span>}
                    <span>{auditor.affiliation === 'external' ? 'External' : 'In-house'}</span>
                  </div>
                </div>

                <StatusPill variant="info" label={typeLabel(a.type)} />

                <span className="appointment-when">
                  {a.fy ? `FY ${a.fy}` : ''}
                  {a.fy ? ' · ' : ''}
                  Appointed {fmtDate(a.appointedOn)}
                </span>

                <Button type="button" variant="secondary" size="sm" onClick={() => remove(a)}>Remove</Button>
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <Modal
          isOpen
          onClose={() => setModalOpen(false)}
          title="Appoint auditor"
          footer={
            <>
              <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button type="button" variant="primary" onClick={appoint} loading={saving}>Appoint</Button>
            </>
          }
        >
          <div className="auditor-form">
            {/* Capacity is chosen first: it decides who is even eligible, and
                which of their ceilings the appointment counts against. */}
            <Select
              label="Audit type"
              value={type}
              onChange={(e) => { setType(e.target.value); setAuditorId(''); }}
            >
              {AUDIT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>

            <Select label="Auditor" value={auditorId} onChange={(e) => setAuditorId(e.target.value)}>
              <option value="">— Select an auditor —</option>
              {eligible.map((a) => (
                <option key={a._id} value={a._id} disabled={isFull(a)}>
                  {a.name} — {describe(a)}
                </option>
              ))}
            </Select>

            {eligible.length === 0 && (
              <p className="auditor-form-hint">
                No active {typeLabel(type).toLowerCase()} auditors yet. Add one under Settings → Auditors.
              </p>
            )}

            <Input
              label="Financial Year"
              value={fy}
              onChange={(e) => setFy(e.target.value)}
              placeholder="e.g. 2026-27"
              helperText="Optional — the year this appointment covers"
            />

            {error && <p className="auditor-form-error">{error}</p>}
          </div>
        </Modal>
      )}
    </Card>
  );
}

export default ClientAuditorsManager;
